import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { mintaTeks } from "../lib/dialogAplikasi";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

/* MASTER-BACA-TANPA-JALUR-UBAH-OWNER-01 (24 Sep 2026) — dua kosakata alasan tertutup, Owner saja:
   `revisi-desain` (Q4/K24) dan `tutup-temuan` (K180 §4). Owner menambah, mengganti nama, dan
   menonaktifkan — tidak ada hapus (baris lain merujuk kodenya); minimal satu tetap aktif.
   Sumber: GET/POST/PATCH /api/master/alasan/{jenis}. */

const JENIS = [
  { kode: "revisi-desain", nama: "Alasan revisi desain" },
  { kode: "tutup-temuan", nama: "Alasan tutup temuan Papan Kontrol" },
  { kode: "kategori-inventaris", nama: "Kategori inventaris operasional" },
];

export default function MasterAlasan() {
  const { jenis = "revisi-desain" } = useParams();
  const [data, setData] = useState({ items: [], nama: "" });
  const [loading, setLoading] = useState(true);
  const [labelBaru, setLabelBaru] = useState("");
  const [sibuk, setSibuk] = useState(null);
  // Pola daftar seragam (28 Sep 2026) — master KECIL: seluruh baris dimuat, cari/urut/halaman di klien.
  // Batas barisnya diawasi `backend/tests/test_batas_master_kecil_lokal.py`.
  const daftar = useDaftar(data.items || [], {
    kunciCari: ["label_id", "code"],
    kolom: [{ kunci: "label_id", judul: "Alasan" }, { kunci: "code", judul: "Kode" },
      { kunci: "is_active", judul: "Status", ambil: (k) => (k.is_active ? "Aktif" : "Nonaktif") }],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/master/alasan/${jenis}`, { params: { semua: 1 } });
      setData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat daftar alasan.")); }
    finally { setLoading(false); }
  }, [jenis]);
  useEffect(() => { load(); }, [load]);

  async function tambah() {
    setSibuk("baru");
    try {
      const r = await api.post(`/master/alasan/${jenis}`, { label: labelBaru.trim() });
      toast.success(`Alasan "${r.data.label_id}" ditambahkan.`);
      setLabelBaru("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Alasan tidak bisa ditambahkan.")); }
    finally { setSibuk(null); }
  }

  async function ubah(k, badan, pesan) {
    setSibuk(k.code);
    try {
      await api.patch(`/master/alasan/${jenis}/${k.code}`, badan);
      toast.success(pesan);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Perubahan ditolak.")); }
    finally { setSibuk(null); }
  }

  async function gantiNama(k) {
    const label = await mintaTeks(`Nama baru untuk alasan "${k.label_id}":`, { awal: k.label_id, multiBaris: false });
    if (!label || label.trim() === k.label_id) return;
    ubah(k, { label: label.trim() }, "Nama alasan diganti.");
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="master-alasan">
      <div>
        <h1 className="text-xl font-semibold">Master {data.nama || "Alasan"}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tidak ada hapus — nonaktifkan. Alasan nonaktif tidak ditawarkan lagi; catatan lama tetap memakai alasannya.
        </p>
        <div className="mt-2 flex flex-wrap gap-3 text-sm">
          {JENIS.map(j => (
            <Link key={j.kode} to={`/master/alasan/${j.kode}`}
                  className={j.kode === jenis ? "font-semibold underline" : "underline text-muted-foreground"}
                  data-testid={`ma-jenis-${j.kode}`}>{j.nama}</Link>
          ))}
          <Link to="/master/kategori-komplain" className="underline text-muted-foreground">Kategori komplain</Link>
        </div>
      </div>

      <Card className="p-3 flex flex-wrap gap-2 items-center">
        <Input className="max-w-xs" placeholder="Nama alasan baru" value={labelBaru}
               onChange={e => setLabelBaru(e.target.value)} data-testid="ma-label-baru" />
        <Button onClick={tambah} disabled={labelBaru.trim().length < 2 || sibuk === "baru"} data-testid="ma-tambah">
          <Plus size={16} className="mr-1" />Tambah
        </Button>
      </Card>

      <PalangDaftar daftar={daftar} placeholder="Cari alasan / kode…" testId="ma-palang"
                    ekspor={{ nama: `alasan_${jenis}`, kolom: daftar.kolom }} />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <ThUrut daftar={daftar} kunci="label_id" className="text-left px-3 py-2">Alasan</ThUrut>
              <ThUrut daftar={daftar} kunci="code" className="text-left px-3 py-2">Kode</ThUrut>
              <ThUrut daftar={daftar} kunci="is_active" className="text-left px-3 py-2">Status</ThUrut>
              <th className="text-right px-3 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-3 py-4 text-muted-foreground">Memuat…</td></tr>}
            {!loading && daftar.total === 0 && <tr><td colSpan={4} className="px-3 py-4 text-muted-foreground">
              {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada alasan."}</td></tr>}
            {!loading && daftar.tampil.map(k => (
              <tr key={k.code} className="border-t" data-testid={`ma-baris-${k.code}`}>
                <td className="px-3 py-2">{k.label_id}</td>
                <td className="px-3 py-2 font-mono text-xs">{k.code}</td>
                <td className="px-3 py-2">
                  {k.is_active ? <Badge variant="secondary">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                  <Button size="sm" variant="outline" disabled={sibuk === k.code} onClick={() => gantiNama(k)}>Ganti nama</Button>
                  <Button size="sm" variant="outline" disabled={sibuk === k.code}
                          onClick={() => ubah(k, { is_active: !k.is_active },
                                              k.is_active ? "Alasan dinonaktifkan." : "Alasan diaktifkan kembali.")}
                          data-testid={`ma-aktif-${k.code}`}>
                    {k.is_active ? "Nonaktifkan" : "Aktifkan"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="ma-paginasi" />
      </Card>
    </div>
  );
}
